import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { FornecedoresService } from '../fornecedores.service';
import { HttpEventType } from '@angular/common/http';

interface UploadEntry {
  file: File;
  preview?: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  response?: any;
  error?: any;
  retries: number;
  sub?: Subscription | null;
  cancel?: () => void;
}

@Component({
  standalone: false,
  selector: 'app-media-manager',
  template: `
    <div class="p-4 bg-white rounded shadow">
      <h3 class="text-lg font-semibold">Gerenciador de Mídias</h3>
      <div *ngIf="!fornecedorId" class="text-sm text-muted mt-2">Crie o Fornecedor antes de enviar mídias.</div>

      <div class="mt-3" *ngIf="fornecedorId">
        <input type="file" (change)="onFiles($event)" multiple accept="image/*" />
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
          <div *ngFor="let u of uploads" class="border p-2 rounded">
            <div class="h-36 bg-surface mb-2 flex items-center justify-center overflow-hidden">
              <img *ngIf="u.preview" [src]="u.preview" class="object-cover h-full w-full" />
              <span *ngIf="!u.preview" class="text-sm text-muted">Preview...</span>
            </div>
            <div class="text-sm mb-2">{{ u.file.name }}</div>
            <div class="h-2 bg-surface rounded overflow-hidden">
              <div class="bg-primary h-2" [style.width.%]="u.progress"></div>
            </div>
            <div class="mt-2 flex items-center justify-between">
              <div class="text-xs text-muted">{{ u.status }}</div>
              <div class="flex gap-2">
                <button *ngIf="u.status === 'error' && u.retries < 3" (click)="retry(u)" class="text-sm px-2 py-1 border rounded">Retry</button>
                <button *ngIf="u.status === 'pending'" (click)="upload(u)" class="text-sm px-2 py-1 bg-primary text-white rounded">Upload</button>
                <button *ngIf="u.status === 'uploading'" (click)="cancelUpload(u)" class="text-sm px-2 py-1 border rounded">Cancelar</button>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4">
          <h4 class="font-medium">Mídias existentes</h4>
          <div *ngIf="medias.length === 0" class="text-sm text-muted">Nenhuma mídia enviada.</div>
          <div class="grid grid-cols-3 gap-3 mt-2">
            <div *ngFor="let m of medias" class="p-2 border rounded relative">
              <img [src]="m.url" class="w-full h-40 object-cover rounded mb-2" />
              <div *ngIf="m.isPrimary" class="absolute top-2 left-2 bg-primary text-white text-xs px-2 py-1 rounded shadow z-10 flex items-center gap-1">
                <span style="font-size:1.1em;">★</span> Primary
              </div>
              <div class="flex justify-between items-center">
                <div class="text-sm">{{ m.filename }}</div>
                <div class="flex gap-2">
                  <button (click)="markPrimary(m)" class="text-xs px-2 py-1 border rounded">Marcar</button>
                  <button (click)="deleteMedia(m)" class="text-xs px-2 py-1 border rounded text-danger">Excluir</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `
})
export class MediaManagerComponent implements OnInit, OnChanges {
  @Input() fornecedorId?: string;
  // allow parent to pre-populate medias when editing
  @Input() initialMedias?: any[];
  @Output() fornecedorReloaded = new EventEmitter<any>();
  uploads: UploadEntry[] = [];
  medias: any[] = [];

  constructor(private svc: FornecedoresService) {}


  ngOnInit(): void {
    if (this.fornecedorId) {
      if (this.initialMedias && this.initialMedias.length) {
        this.medias = this.initialMedias;
      } else {
        this.loadMedias();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fornecedorId'] && !changes['fornecedorId'].isFirstChange()) {
      const id = changes['fornecedorId'].currentValue;
      if (id) {
        if (this.initialMedias && this.initialMedias.length) this.medias = this.initialMedias;
        else this.loadMedias();
      }
    }

    if (changes['initialMedias'] && !changes['initialMedias'].isFirstChange()) {
      const imgs = changes['initialMedias'].currentValue as any[];
      if (imgs && imgs.length) this.medias = imgs;
    }
  }

  onFiles(ev: any) {
    const files: File[] = Array.from(ev.target.files || []);
    files.forEach(f => {
      const entry: UploadEntry = { file: f, progress: 0, status: 'pending', retries: 0 };
      // generate preview
      const reader = new FileReader();
      reader.onload = e => entry.preview = e.target?.result as string;
      reader.readAsDataURL(f);
      this.uploads.push(entry);
    });
  }

  upload(entry: UploadEntry) {
    if (!this.fornecedorId) return;
    entry.status = 'uploading';
    const fd = new FormData();
    fd.append('file', entry.file);
    fd.append('fornecedorId', this.fornecedorId!);
    fd.append('filename', entry.file.name);
    fd.append('contentType', entry.file.type || 'image');
    fd.append('IsPrimary', 'false');

    const { events$, cancel } = this.svc.uploadMediaWithCancel(fd, this.fornecedorId!, { retries: 3, backoffMs: 1200 });
    entry.cancel = cancel;
    entry.status = 'uploading';
    entry.progress = 0;
    entry.sub = events$.subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          entry.progress = Math.round((100 * event.loaded) / (event.total || event.loaded));
        } else if (event.type === HttpEventType.Response) {
          entry.status = 'success';
          entry.response = event.body;
          // reload full fornecedor to keep medias in sync (and emit to parent)
          this.loadMedias();
        }
      },
      error: (err: any) => {
        entry.status = 'error';
        entry.error = err;
        entry.retries += 1;
      },
      complete: () => {
        if (entry.status !== 'error') entry.status = 'success';
      }
    });
  }

  retry(entry: UploadEntry) { this.upload(entry); }

  loadMedias() {
    // Prefer reloading the full fornecedor (admin GET) which returns medias in the payload.
    this.svc.adminGet(this.fornecedorId!).subscribe({
      next: (res: any) => {
        this.medias = res?.medias || res?.imagens || (res.data || []);
        this.fornecedorReloaded.emit(res);
      },
      error: () => {
        // fallback to the older getMedia endpoint if adminGet fails
        this.svc.getMedia(this.fornecedorId!).subscribe((res: any) => (this.medias = res.data || res || []));
      }
    });
  }

  deleteMedia(m: any) {
    if (!confirm('Excluir mídia?')) return;
    this.svc.deleteMedia(m.id).subscribe(() => this.loadMedias());
  }

  markPrimary(m: any) {
    this.svc.markPrimary(m.id).subscribe(() => {
      // reload fornecedor so UI reflects updated primary
      this.loadMedias();
    });
  }

  cancelUpload(entry: UploadEntry) {
    try {
      entry.cancel?.();
    } catch (e) {}
    entry.sub?.unsubscribe();
    entry.status = 'pending';
  }
}

